/**
 * Quiz Generator - Creates quizzes from article text without external LLM
 * Uses text analysis and pattern matching to generate questions
 */

export interface QuizQuestion {
  id: number;
  type: "fill-blank" | "true-false" | "multiple-choice";
  question: string;
  options?: string[];
  correctAnswer: string | boolean;
  explanation: string;
  originalSentence: string;
}

export interface Quiz {
  articleTitle: string;
  questions: QuizQuestion[];
  generatedAt: string;
}

/**
 * Extract sentences from article text
 */
function extractSentences(text: string): string[] {
  // Remove wiki-style headings
  const cleanedText = text.replace(/={2,}[^=]+={2,}/g, " ");
  
  // Split into sentences (handling abbreviations and decimals)
  const sentences = cleanedText
    .replace(/([.!?])\s+/g, "$1|SPLIT|")
    .split("|SPLIT|")
    .map((s) => s.trim())
    .filter((s) => {
      // Filter out short sentences and ones without useful content
      return (
        s.length > 30 &&
        s.length < 300 &&
        /[a-zA-Z]/.test(s) &&
        !s.startsWith("See also") &&
        !s.startsWith("References") &&
        !s.startsWith("External links")
      );
    });

  return sentences;
}

/**
 * Extract key facts from sentences (names, dates, numbers, places)
 */
function extractKeyFacts(sentence: string): {
  dates: string[];
  numbers: string[];
  properNouns: string[];
  keywords: string[];
} {
  // Extract years (4 digits)
  const dates = sentence.match(/\b(1[0-9]{3}|20[0-2][0-9])\b/g) || [];
  
  // Extract numbers with context
  const numbers = sentence.match(/\b\d+(?:,\d{3})*(?:\.\d+)?(?:\s*(?:percent|%|million|billion|thousand|km|miles|meters|feet|years|days|months))?\b/gi) || [];
  
  // Extract proper nouns (capitalized words not at sentence start)
  const words = sentence.split(/\s+/);
  const properNouns: string[] = [];
  for (let i = 1; i < words.length; i++) {
    const word = words[i].replace(/[^a-zA-Z]/g, "");
    if (word.length > 2 && /^[A-Z][a-z]+$/.test(word)) {
      properNouns.push(word);
    }
  }
  
  // Extract important keywords (longer words that aren't common)
  const commonWords = new Set([
    "the", "and", "was", "were", "been", "being", "have", "has", "had",
    "does", "did", "will", "would", "could", "should", "may", "might",
    "must", "shall", "can", "need", "this", "that", "these", "those",
    "with", "from", "into", "during", "before", "after", "above", "below",
    "between", "under", "again", "further", "then", "once", "here", "there",
    "when", "where", "which", "while", "about", "against", "through",
    "during", "before", "after", "because", "although", "however", "therefore",
    "their", "they", "them", "what", "some", "other", "such", "only", "same",
    "also", "than", "very", "just", "over", "most", "more", "many", "much"
  ]);
  
  const keywords = words
    .map((w) => w.replace(/[^a-zA-Z]/g, "").toLowerCase())
    .filter((w) => w.length > 5 && !commonWords.has(w));

  return { dates, numbers, properNouns, keywords: [...new Set(keywords)] };
}

/**
 * Generate a fill-in-the-blank question
 */
function generateFillBlank(
  sentence: string,
  facts: ReturnType<typeof extractKeyFacts>,
  id: number
): QuizQuestion | null {
  // Prefer dates, then proper nouns, then numbers
  let blankWord: string | null = null;
  
  if (facts.dates.length > 0) {
    blankWord = facts.dates[0];
  } else if (facts.properNouns.length > 0) {
    blankWord = facts.properNouns[Math.floor(Math.random() * facts.properNouns.length)];
  } else if (facts.numbers.length > 0) {
    blankWord = facts.numbers[0];
  }
  
  if (!blankWord) return null;
  
  const question = sentence.replace(
    new RegExp(`\\b${blankWord.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i"),
    "_____"
  );
  
  if (question === sentence) return null; // Replacement didn't work
  
  return {
    id,
    type: "fill-blank",
    question: `Fill in the blank: ${question}`,
    correctAnswer: blankWord,
    explanation: `The complete sentence is: "${sentence}"`,
    originalSentence: sentence,
  };
}

/**
 * Generate a true/false question
 */
function generateTrueFalse(
  sentence: string,
  facts: ReturnType<typeof extractKeyFacts>,
  id: number,
  allSentences: string[]
): QuizQuestion | null {
  const isTrue = Math.random() > 0.5;
  
  if (isTrue) {
    // True question - use the original sentence
    return {
      id,
      type: "true-false",
      question: `True or False: ${sentence}`,
      options: ["True", "False"],
      correctAnswer: true,
      explanation: `This statement is true as stated in the article.`,
      originalSentence: sentence,
    };
  } else {
    // False question - modify a fact
    let modifiedSentence = sentence;
    let wasModified = false;
    
    if (facts.dates.length > 0) {
      const originalDate = facts.dates[0];
      const fakeDate = String(parseInt(originalDate) + (Math.random() > 0.5 ? 10 : -10));
      modifiedSentence = sentence.replace(originalDate, fakeDate);
      wasModified = modifiedSentence !== sentence;
    } else if (facts.numbers.length > 0) {
      const originalNum = facts.numbers[0];
      const numMatch = originalNum.match(/\d+/);
      if (numMatch) {
        const num = parseInt(numMatch[0]);
        const fakeNum = String(Math.floor(num * (Math.random() > 0.5 ? 1.5 : 0.5)));
        modifiedSentence = sentence.replace(numMatch[0], fakeNum);
        wasModified = modifiedSentence !== sentence;
      }
    }
    
    if (!wasModified) {
      // If we couldn't modify, just make it true
      return {
        id,
        type: "true-false",
        question: `True or False: ${sentence}`,
        options: ["True", "False"],
        correctAnswer: true,
        explanation: `This statement is true as stated in the article.`,
        originalSentence: sentence,
      };
    }
    
    return {
      id,
      type: "true-false",
      question: `True or False: ${modifiedSentence}`,
      options: ["True", "False"],
      correctAnswer: false,
      explanation: `This is false. The correct statement is: "${sentence}"`,
      originalSentence: sentence,
    };
  }
}

/**
 * Generate a multiple choice question
 */
function generateMultipleChoice(
  sentence: string,
  facts: ReturnType<typeof extractKeyFacts>,
  id: number,
  allFacts: ReturnType<typeof extractKeyFacts>[]
): QuizQuestion | null {
  // Find a fact to ask about
  let correctAnswer: string | null = null;
  let questionText: string | null = null;
  
  if (facts.properNouns.length > 0) {
    correctAnswer = facts.properNouns[0];
    questionText = sentence.replace(
      new RegExp(`\\b${correctAnswer}\\b`, "i"),
      "what/who"
    );
  } else if (facts.dates.length > 0) {
    correctAnswer = facts.dates[0];
    questionText = sentence.replace(correctAnswer, "when");
  }
  
  if (!correctAnswer || questionText === sentence) return null;
  
  // Generate wrong answers from other sentences' facts
  const wrongAnswers: string[] = [];
  const usedAnswers = new Set([correctAnswer.toLowerCase()]);
  
  for (const otherFacts of allFacts) {
    const candidates = [...otherFacts.properNouns, ...otherFacts.dates];
    for (const candidate of candidates) {
      if (!usedAnswers.has(candidate.toLowerCase()) && wrongAnswers.length < 3) {
        wrongAnswers.push(candidate);
        usedAnswers.add(candidate.toLowerCase());
      }
    }
    if (wrongAnswers.length >= 3) break;
  }
  
  // If we don't have enough wrong answers, generate some
  while (wrongAnswers.length < 3) {
    if (facts.dates.length > 0) {
      const baseYear = parseInt(facts.dates[0]);
      wrongAnswers.push(String(baseYear + (wrongAnswers.length + 1) * 5));
    } else {
      wrongAnswers.push(`Option ${wrongAnswers.length + 1}`);
    }
  }
  
  // Shuffle options
  const options = [correctAnswer, ...wrongAnswers.slice(0, 3)];
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }
  
  return {
    id,
    type: "multiple-choice",
    question: `Based on the article: ${questionText}?`,
    options,
    correctAnswer,
    explanation: `The answer is "${correctAnswer}". From the article: "${sentence}"`,
    originalSentence: sentence,
  };
}

/**
 * Generate a quiz from article content
 */
export function generateQuiz(articleTitle: string, articleContent: string, numQuestions: number = 5): Quiz {
  const sentences = extractSentences(articleContent);
  const allFacts = sentences.map(extractKeyFacts);
  
  // Filter sentences that have extractable facts
  const usableSentences: { sentence: string; facts: ReturnType<typeof extractKeyFacts>; index: number }[] = [];
  
  sentences.forEach((sentence, index) => {
    const facts = allFacts[index];
    if (facts.dates.length > 0 || facts.properNouns.length > 0 || facts.numbers.length > 0) {
      usableSentences.push({ sentence, facts, index });
    }
  });
  
  // Shuffle and pick sentences
  const shuffled = [...usableSentences].sort(() => Math.random() - 0.5);
  const selected = shuffled.slice(0, Math.min(numQuestions * 2, shuffled.length));
  
  const questions: QuizQuestion[] = [];
  let questionId = 1;
  
  for (const { sentence, facts } of selected) {
    if (questions.length >= numQuestions) break;
    
    // Rotate through question types
    const questionType = questions.length % 3;
    let question: QuizQuestion | null = null;
    
    switch (questionType) {
      case 0:
        question = generateFillBlank(sentence, facts, questionId);
        break;
      case 1:
        question = generateTrueFalse(sentence, facts, questionId, sentences);
        break;
      case 2:
        question = generateMultipleChoice(sentence, facts, questionId, allFacts);
        break;
    }
    
    if (question) {
      questions.push(question);
      questionId++;
    }
  }
  
  // If we still need more questions, try other types
  for (const { sentence, facts } of selected) {
    if (questions.length >= numQuestions) break;
    
    // Try fill-blank first
    let question = generateFillBlank(sentence, facts, questionId);
    if (!question) {
      question = generateTrueFalse(sentence, facts, questionId, sentences);
    }
    
    if (question && !questions.some((q) => q.originalSentence === sentence)) {
      questions.push(question);
      questionId++;
    }
  }
  
  return {
    articleTitle,
    questions: questions.slice(0, numQuestions),
    generatedAt: new Date().toISOString(),
  };
}
